import { useState, useEffect } from 'react';
import { casiaAPI, parcellaireGeoAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import {
  Leaf,
  Bug,
  Droplets,
  Thermometer,
  AlertTriangle,
  CheckCircle,
  Send,
  RefreshCw,
  Euro,
  Clock,
  Beaker,
  Shield,
  Zap,
  ChevronRight,
  Star,
  MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';

// Couleurs par famille
const familleColors = {
  insectes_ravageurs: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  maladies_fongiques: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  stress_abiotiques: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  fertilisation_stimulation: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  hygiene_process: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
};

const familleLabels = {
  insectes_ravageurs: 'Insectes & Ravageurs',
  maladies_fongiques: 'Maladies Fongiques',
  stress_abiotiques: 'Stress Abiotiques',
  fertilisation_stimulation: 'Fertilisation',
  hygiene_process: 'Hygiène & Process',
};

const ProduitCard = ({ produit, onClick }) => (
  <Card 
    className="card-hover cursor-pointer" 
    onClick={() => onClick?.(produit)}
    data-testid={`produit-card-${produit.id}`}
  >
    <CardContent className="pt-6">
      <div className="flex items-start justify-between mb-3">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <Beaker className="w-6 h-6 text-primary" />
        </div>
        <Badge className={familleColors[produit.famille]}>
          {familleLabels[produit.famille]?.split(' ')[0]}
        </Badge>
      </div>
      <h3 className="font-bold text-xl text-primary">{produit.nom}</h3>
      <p className="text-sm text-muted-foreground mt-1">{produit.type_produit}</p>
      <div className="flex flex-wrap gap-1 mt-3">
        {produit.cibles?.slice(0, 3).map((cible, i) => (
          <Badge key={i} variant="outline" className="text-xs">
            {cible}
          </Badge>
        ))}
        {produit.cibles?.length > 3 && (
          <Badge variant="outline" className="text-xs">
            +{produit.cibles.length - 3}
          </Badge>
        )}
      </div>
      <div className="mt-4 pt-3 border-t flex justify-between items-center">
        <div className="flex items-center gap-1">
          {produit.compatible_bio && (
            <Badge className="bg-green-600 text-white text-xs">BIO</Badge>
          )}
          {produit.pnpp && (
            <Badge variant="outline" className="text-xs">PNPP</Badge>
          )}
        </div>
        <span className="font-semibold">{produit.prix_unitaire_ht}€</span>
      </div>
    </CardContent>
  </Card>
);

const RecommandationCard = ({ reco }) => (
  <Card className="border-l-4 border-l-primary">
    <CardContent className="pt-4">
      <div className="flex items-start justify-between">
        <div>
          <Badge variant="outline" className="mb-2">
            Priorité {reco.priorite}
          </Badge>
          <h4 className="font-bold text-lg text-primary">{reco.produit_nom}</h4>
        </div>
        <div className="text-right">
          <span className="font-bold text-lg">{reco.dose}</span>
          <span className="text-muted-foreground text-sm ml-1">{reco.unite}</span>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mt-2">{reco.raison}</p>
      <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
        <Clock className="w-3 h-3" />
        <span>{reco.moment}</span>
      </div>
    </CardContent>
  </Card>
);

const AnalyseRapide = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    culture: 'vigne',
    problemes: [],
    stade: '',
    temperature: 18,
    humidite: 60,
    pluie_prevue: false,
    gel_prevu: false,
    objectif: 'conventionnel',
  });

  const problemesOptions = [
    { value: 'pucerons', label: 'Pucerons' },
    { value: 'mildiou', label: 'Mildiou' },
    { value: 'oïdium', label: 'Oïdium' },
    { value: 'botrytis', label: 'Botrytis' },
    { value: 'thrips', label: 'Thrips' },
    { value: 'altises', label: 'Altises' },
    { value: 'mouche des fruits', label: 'Mouche des fruits' },
  ];

  const handleProblemeToggle = (value) => {
    setForm(prev => ({
      ...prev,
      problemes: prev.problemes.includes(value)
        ? prev.problemes.filter(p => p !== value)
        : [...prev.problemes, value]
    }));
  };

  const handleAnalyse = async () => {
    setLoading(true);
    try {
      const res = await casiaAPI.analyseRapide(form);
      setResult(res.data);
      toast.success('Analyse terminée');
    } catch (error) {
      toast.error('Erreur lors de l\'analyse');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Formulaire */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            Analyse Rapide
          </CardTitle>
          <CardDescription>
            Obtenez des recommandations CASIA en quelques clics
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Culture */}
          <div className="space-y-2">
            <Label>Culture</Label>
            <Select value={form.culture} onValueChange={(v) => setForm({...form, culture: v})}>
              <SelectTrigger data-testid="culture-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vigne">Vigne</SelectItem>
                <SelectItem value="arboriculture">Arboriculture</SelectItem>
                <SelectItem value="maraîchage">Maraîchage</SelectItem>
                <SelectItem value="grandes cultures">Grandes cultures</SelectItem>
                <SelectItem value="cerisier">Cerisier</SelectItem>
                <SelectItem value="olivier">Olivier</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Stade */}
          <div className="space-y-2">
            <Label>Stade phénologique</Label>
            <Select value={form.stade} onValueChange={(v) => setForm({...form, stade: v})}>
              <SelectTrigger data-testid="stade-select">
                <SelectValue placeholder="Sélectionner un stade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="debourrement">Débourrement</SelectItem>
                <SelectItem value="floraison">Floraison</SelectItem>
                <SelectItem value="nouaison">Nouaison</SelectItem>
                <SelectItem value="veraison">Véraison</SelectItem>
                <SelectItem value="maturation">Maturation</SelectItem>
                <SelectItem value="recolte">Récolte</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Problèmes observés */}
          <div className="space-y-2">
            <Label>Problèmes observés</Label>
            <div className="grid grid-cols-2 gap-2">
              {problemesOptions.map((opt) => (
                <div key={opt.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={opt.value}
                    checked={form.problemes.includes(opt.value)}
                    onCheckedChange={() => handleProblemeToggle(opt.value)}
                  />
                  <label htmlFor={opt.value} className="text-sm cursor-pointer">
                    {opt.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Météo */}
          <div className="space-y-4">
            <Label>Conditions météo</Label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Température</span>
                  <span className="text-sm font-medium">{form.temperature}°C</span>
                </div>
                <Slider
                  value={[form.temperature]}
                  onValueChange={([v]) => setForm({...form, temperature: v})}
                  min={-5}
                  max={40}
                  step={1}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Humidité</span>
                  <span className="text-sm font-medium">{form.humidite}%</span>
                </div>
                <Slider
                  value={[form.humidite]}
                  onValueChange={([v]) => setForm({...form, humidite: v})}
                  min={0}
                  max={100}
                  step={5}
                />
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="pluie"
                  checked={form.pluie_prevue}
                  onCheckedChange={(v) => setForm({...form, pluie_prevue: v})}
                />
                <label htmlFor="pluie" className="text-sm cursor-pointer">
                  Pluie prévue
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="gel"
                  checked={form.gel_prevu}
                  onCheckedChange={(v) => setForm({...form, gel_prevu: v})}
                />
                <label htmlFor="gel" className="text-sm cursor-pointer">
                  Gel prévu
                </label>
              </div>
            </div>
          </div>

          {/* Objectif */}
          <div className="space-y-2">
            <Label>Objectif</Label>
            <Select value={form.objectif} onValueChange={(v) => setForm({...form, objectif: v})}>
              <SelectTrigger data-testid="objectif-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="conventionnel">Conventionnel</SelectItem>
                <SelectItem value="bio">Agriculture Bio</SelectItem>
                <SelectItem value="reduction_phyto">Réduction phyto</SelectItem>
                <SelectItem value="zero_residu">Zéro résidu</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button 
            className="w-full gap-2" 
            onClick={handleAnalyse}
            disabled={loading}
            data-testid="analyse-button"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Zap className="w-4 h-4" />
            )}
            Analyser la situation
          </Button>
        </CardContent>
      </Card>

      {/* Résultats */}
      <div className="space-y-4">
        {result ? (
          <>
            {/* Alertes */}
            {result.alertes?.length > 0 && (
              <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-amber-800 dark:text-amber-400">
                        Alerte
                      </h4>
                      {result.alertes.map((a, i) => (
                        <p key={i} className="text-sm text-amber-700 dark:text-amber-300">
                          {a.message}
                        </p>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Message principal */}
            <Card className="bg-primary/5 border-primary">
              <CardContent className="pt-4">
                <p className="text-lg">{result.message}</p>
              </CardContent>
            </Card>

            {/* Recommandations */}
            {result.recommandations?.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">Produits recommandés</h3>
                {result.recommandations.map((reco, i) => (
                  <RecommandationCard key={i} reco={reco} />
                ))}
              </div>
            )}

            {/* Protocole suggéré */}
            {result.protocole_suggere && (
              <Card className="bg-muted/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Protocole suggéré
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <h4 className="font-semibold">{result.protocole_suggere.nom}</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    {result.protocole_suggere.description}
                  </p>
                  <div className="space-y-2">
                    {result.protocole_suggere.etapes?.map((etape, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <Badge variant="outline">J{etape.jour >= 0 ? '+' : ''}{etape.jour}</Badge>
                        <span className="font-medium">{etape.produit_id?.replace('casia-', '').toUpperCase()}</span>
                        <span className="text-muted-foreground">- {etape.objectif}</span>
                      </div>
                    ))}
                  </div>
                  {result.protocole_suggere.cout_hectare && (
                    <div className="mt-3 pt-3 border-t flex items-center gap-2">
                      <Euro className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">
                        Coût estimé: <strong>{result.protocole_suggere.cout_hectare}€/ha</strong>
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Analyse détaillée */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Analyse détaillée</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Culture:</span>
                    <span className="ml-2 font-medium">{result.analyse?.culture}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Risque fongique:</span>
                    <Badge 
                      variant="outline" 
                      className={`ml-2 ${
                        result.analyse?.risque_fongique === 'eleve' ? 'border-red-500 text-red-500' :
                        result.analyse?.risque_fongique === 'moyen' ? 'border-amber-500 text-amber-500' :
                        'border-green-500 text-green-500'
                      }`}
                    >
                      {result.analyse?.risque_fongique}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
              <Beaker className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Aucune analyse</h3>
            <p className="text-muted-foreground">
              Remplissez le formulaire et lancez une analyse<br />
              pour obtenir des recommandations CASIA
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const CASIAPage = () => {
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [familleFilter, setFamilleFilter] = useState('all');
  const [selectedProduit, setSelectedProduit] = useState(null);

  useEffect(() => {
    const fetchProduits = async () => {
      try {
        const res = await casiaAPI.getProduits();
        setProduits(res.data);
      } catch (error) {
        toast.error('Erreur lors du chargement des produits');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduits();
  }, []);

  const filteredProduits = produits.filter(p => 
    familleFilter === 'all' || p.famille === familleFilter
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="casia-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Leaf className="w-6 h-6 text-primary-foreground" />
            </div>
            CASIA
          </h1>
          <p className="text-muted-foreground mt-1">
            Solutions naturelles pour l'agriculture • {produits.length} produits disponibles
          </p>
        </div>
      </div>

      <Tabs defaultValue="analyse" className="space-y-6">
        <TabsList>
          <TabsTrigger value="analyse" data-testid="tab-analyse">
            <Zap className="w-4 h-4 mr-2" />
            Analyse & Recommandations
          </TabsTrigger>
          <TabsTrigger value="produits" data-testid="tab-produits">
            <Beaker className="w-4 h-4 mr-2" />
            Catalogue Produits
          </TabsTrigger>
        </TabsList>

        <TabsContent value="analyse">
          <AnalyseRapide />
        </TabsContent>

        <TabsContent value="produits" className="space-y-6">
          {/* Filtres */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={familleFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFamilleFilter('all')}
            >
              Tous
            </Button>
            {Object.entries(familleLabels).map(([key, label]) => (
              <Button
                key={key}
                variant={familleFilter === key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFamilleFilter(key)}
              >
                {label}
              </Button>
            ))}
          </div>

          {/* Grille produits */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-48 skeleton rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredProduits.map((produit) => (
                <ProduitCard 
                  key={produit.id} 
                  produit={produit} 
                  onClick={setSelectedProduit}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog détail produit */}
      <Dialog open={!!selectedProduit} onOpenChange={() => setSelectedProduit(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl font-bold text-primary">{selectedProduit?.nom}</span>
              {selectedProduit?.compatible_bio && (
                <Badge className="bg-green-600 text-white">BIO</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedProduit && (
            <div className="space-y-6">
              <div>
                <Badge className={familleColors[selectedProduit.famille]}>
                  {familleLabels[selectedProduit.famille]}
                </Badge>
                <p className="mt-2 text-muted-foreground">{selectedProduit.type_produit}</p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Action</h4>
                <p className="text-sm">{selectedProduit.description_action}</p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Cibles</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedProduit.cibles?.map((c, i) => (
                    <Badge key={i} variant="outline">{c}</Badge>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Moment d'intervention</h4>
                <p className="text-sm">{selectedProduit.description_moment}</p>
              </div>

              <div className="grid grid-cols-3 gap-4 bg-muted/50 rounded-lg p-4">
                <div>
                  <p className="text-sm text-muted-foreground">Dose recommandée</p>
                  <p className="font-bold">{selectedProduit.dose_recommandee} {selectedProduit.unite_dose}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Applications max</p>
                  <p className="font-bold">{selectedProduit.nb_applications_max}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Prix</p>
                  <p className="font-bold">{selectedProduit.prix_unitaire_ht}€ / {selectedProduit.conditionnement}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CASIAPage;
